/**
 * jizaipad-api のエラーレスポンス（RFC 9457 Problem Details）を読むための helper。
 *
 * 正の形状は `{ title, status, detail, errors: [{ field, code, message }] }` で、
 * Content-Type は `application/problem+json`。移行期間中は旧キーの `message`（detail と同値）と
 * `error`（認証・認可系）が deprecated として併存する。
 * 旧 Interactor 経路の `errors: string[]` もまだ返り得るため、両方を受ける。
 */

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return value !== null && typeof value === 'object'
}

const asText = (value: unknown): string | null => {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

// axios エラーから response.data を取り出す。axios 以外・ネットワークエラーでは null
const getResponseData = (error: unknown): Record<string, unknown> | null => {
  if (!isRecord(error)) return null
  const response = error.response
  if (!isRecord(response)) return null
  return isRecord(response.data) ? response.data : null
}

/**
 * `errors` の各要素からメッセージを取り出す。
 * RFC 9457 のオブジェクト形式（`{ field, code, message }`）と旧 string 形式の両方を受ける。
 */
const getErrorEntryMessage = (item: unknown): string | null => {
  if (typeof item === 'string') return asText(item)
  if (isRecord(item)) return asText(item.message)
  return null
}

/** snake_case の field 名をフォームの camelCase キーへ寄せる（serial_number → serialNumber） */
const toCamelCase = (value: string): string => {
  return value.replace(/_([a-z])/g, (_match, char: string) => char.toUpperCase())
}

/**
 * API エラーの表示用メッセージを取得する。
 *
 * 優先順は `detail`（RFC 9457 の正）→ `message` / `error`（deprecated な併存キー）→ `errors` の連結。
 * どれも取れなければ null を返すので、呼び出し側で既定文言へフォールバックする。
 */
export const getApiErrorMessage = (error: unknown): string | null => {
  const data = getResponseData(error)
  if (data == null) return null

  const detail = asText(data.detail) ?? asText(data.message) ?? asText(data.error)
  if (detail != null) return detail

  if (Array.isArray(data.errors)) {
    const messages = data.errors
      .map(getErrorEntryMessage)
      .filter((message): message is string => message != null)
    if (messages.length > 0) return messages.join('\n')
  }

  return null
}

/**
 * `errors: [{ field, code, message }]` を field 名で引ける形にして返す。
 * フォームの項目単位でエラーを出したいときに使う。
 *
 * - field 名は camelCase へ正規化する（API は snake_case を返す）
 * - `field: 'base'` は特定の項目に紐づかないので含めない（呼び出し側は
 *   {@link getApiErrorMessage} で全体メッセージとして表示する）
 * - 同じ field が複数あれば最初の 1 件を採る
 */
export const getApiFieldErrorMessages = (error: unknown): Record<string, string> => {
  const data = getResponseData(error)
  if (data == null || !Array.isArray(data.errors)) return {}

  return data.errors.reduce<Record<string, string>>((acc, item) => {
    if (!isRecord(item)) return acc

    const field = asText(item.field)
    const message = asText(item.message)
    if (field == null || message == null || field === 'base') return acc

    const key = toCamelCase(field)
    if (key in acc) return acc

    acc[key] = message
    return acc
  }, {})
}

/**
 * API エラーの HTTP ステータスを取得する。axios 以外・ネットワークエラーでは null。
 *
 * 認証系（ログイン・MFA・パスワード再設定など）は status で分岐するため、
 * `error.response.status` の参照をここへ集約する。
 */
export const getApiErrorStatus = (error: unknown): number | null => {
  if (!isRecord(error)) return null
  const response = error.response
  if (!isRecord(response)) return null
  return typeof response.status === 'number' ? response.status : null
}

/**
 * `errors[0].code` を取得する。取れなければ null。
 *
 * 同じ status でも意味が分かれる応答（403 `recaptcha_failed`、401 `mfa_code_invalid` など）を
 * 判別するのに使う。文言（detail）での判定は API 側の文言変更で壊れるため code で判定する。
 */
export const getApiErrorCode = (error: unknown): string | null => {
  const data = getResponseData(error)
  if (data == null || !Array.isArray(data.errors)) return null
  const first: unknown = data.errors[0]
  return isRecord(first) ? asText(first.code) : null
}

/**
 * `errors[].code` をすべて取得する。取れなければ空配列。
 *
 * 1 つの応答が複数の code を返す場合（例: `internal_server_error` と `forge_server_unavailable`）に、
 * 先頭以外の code で判定したいときに使う。
 */
export const getApiErrorCodes = (error: unknown): string[] => {
  const data = getResponseData(error)
  if (data == null || !Array.isArray(data.errors)) return []
  return data.errors
    .map((item: unknown) => (isRecord(item) ? asText(item.code) : null))
    .filter((code): code is string => code != null)
}
